import { useEffect, useState } from 'react';

export const ROTATING_PLACEHOLDER_INTERVAL_MS = 6000;

interface UseRotatingPlaceholderArgs {
  count: number;
  /** Holds the current line in place (the editor is focused). */
  paused: boolean;
  /** Reduced motion pins the first line and never rotates. */
  reducedMotion: boolean;
  intervalMs?: number;
}

export interface RotatingPlaceholderState {
  activeIndex: number;
  /** The line fading out, or null before the first rotation. */
  previousIndex: number | null;
}

const INITIAL_STATE: RotatingPlaceholderState = { activeIndex: 0, previousIndex: null };

export const useRotatingPlaceholder = ({
  count,
  paused,
  reducedMotion,
  intervalMs = ROTATING_PLACEHOLDER_INTERVAL_MS,
}: UseRotatingPlaceholderArgs): RotatingPlaceholderState => {
  const [state, setState] = useState(INITIAL_STATE);
  const canRotate = !reducedMotion && count > 1;

  useEffect(() => {
    if (!canRotate || paused) return;
    const timer = window.setInterval(() => {
      setState(({ activeIndex }) => ({
        activeIndex: (activeIndex + 1) % count,
        previousIndex: activeIndex,
      }));
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [canRotate, count, intervalMs, paused]);

  return canRotate ? state : INITIAL_STATE;
};
